// What each phase of a cable should measure, derived from the one beneath it.
//
// A cable is built outward: a wire, insulation around it, several of those laid
// up into a rope, a sheath over the rope, armour over that, a sheath over the
// armour. Every diameter therefore follows from the previous one, and the whole
// chain is rooted at the insulated conductor.
//
// The prediction deliberately runs off each phase's EXPECTED diameter rather
// than its stored one. Chaining stored values would let one typo look correct
// all the way out, because every later phase would agree with the mistake.
// Running the cascade independently makes a wrong stored value visible as a
// disagreement at exactly the phase where it was introduced.
//
// Extracted from the create-standard form so the wizard cannot drift from it —
// there is one engine, and it is unit-tested.

const PI = Math.PI;

export const disc = d => (PI * Number(d) * Number(d)) / 4;
export const round4 = n => Number(Number(n).toFixed(4));

/**
 * The wall thickness to assume from a min/max tolerance pair.
 *
 * Standards record a permitted range; the cascade needs a single number, and
 * the middle of the range is what the line is set to aim for. With only one of
 * the two given, that one is the whole story.
 */
export const avgRadial = (min, max) => {
  const mn = Number(min) || 0;
  const mx = Number(max) || 0;
  if (mn && mx) return (mn + mx) / 2;
  return mn || mx || 0;
};

/**
 * How much wider a bundle of n round conductors is than one of them.
 *
 * The circumscribed circle of n equal circles arranged in a ring. Two sit side
 * by side (twice the diameter); three, four and five have their own closed
 * forms; beyond that the ring formula holds.
 */
export const packRatio = n => {
  if (n <= 1) return 1;
  if (n === 2) return 2;
  return 1 + 1 / Math.sin(PI / n);
};

/** Insulated conductor: the wire plus a wall on each side. */
export const estimateIsolationDiameter = (wireDiameter, minRadial, maxRadial) => {
  const d = Number(wireDiameter);
  const r = avgRadial(minRadial, maxRadial);
  if (!d || !r) return null;
  return round4(d + 2 * r);
};

/**
 * The copper cross-section of one conductor, in mm².
 *
 * NOT disc(wire.diameter). A stranded or multi-wire conductor is a bundle with
 * air between the strands, so its outer circle is bigger than its metal: CYYF
 * 5x25 measures Ø6.1 — a disc of 29.22 mm² — around 1+6 wires of Ø1.7/2.14 that
 * come to 23.85 mm² of copper, and 23.82 is what the standard stores. Deriving
 * the section from the outer diameter would overstate it by a fifth, and that
 * number is what the insulation's fillingSection is measured against.
 *
 * A solid conductor is the one case where the two agree, because there is no
 * bundle — the wire IS the circle.
 */
export const wireMetalArea = wire => {
  const d = Number(wire?.diameter) || 0;

  // TYPE decides before the fields do. A solid conductor has no strands, so a
  // gauge list on one is a leftover, not a description: CYYF 4x2.5 declares
  // SINGLE-WIRE with `diameter: 1.7` and a stray `wireDiameter: [1.672]`, two
  // widths for one wire. Reading the list there would quietly narrow the copper.
  // cableGeometry.js:59 branches on type first for the same reason.
  if (String(wire?.type ?? '').toUpperCase() === 'SINGLE-WIRE') {
    return d > 0 ? round4(disc(d)) : 0;
  }

  const gauges = Array.isArray(wire?.wireDiameter)
    ? wire.wireDiameter
    : wire?.wireDiameter != null ? [wire.wireDiameter] : [];
  const counts = Array.isArray(wire?.numberOfWires)
    ? wire.numberOfWires
    : wire?.numberOfWires != null ? [wire.numberOfWires] : [];

  if (gauges.length) {
    let a = 0;
    gauges.forEach((g, i) => {
      a += (Number(counts[i]) || 1) * disc(Number(g) || 0);
    });
    if (a > 0) return round4(a);
  }
  return d > 0 ? round4(disc(d)) : 0;
};

/**
 * The copper a stranding phase puts out, in mm².
 *
 * Same idea as wireMetalArea with one extra turn: on a STRANDED or FLEXIBLE
 * stranding phase `numberOfWires` is the count PER DRUM, and the rope is all the
 * drums together. COPPER_ROPE_FLEXIBLE 1x25 stores 26 × Ø0.4 over 7 drums — 182
 * wires, 22.87 mm² — and its section says 22.8. Read without the drums it looks
 * like a sevenfold shortfall. redis_dev/helpers/jobHelpers.js:904 does the same
 * multiplication when it works out what to draw from the warehouse.
 *
 * A MULTI-WIRE stranding is one compacted rope, not a set of drums, so its count
 * is already the total.
 */
export const strandingMetalArea = phase => {
  const perDrum = wireMetalArea(phase);
  if (!(perDrum > 0)) return 0;
  const type = String(phase?.type ?? '').toUpperCase();
  if (type !== 'STRANDED' && type !== 'FLEXIBLE') return perDrum;
  const drums = Number(phase?.numberOfDrums) || 1;
  return round4(perDrum * drums);
};

/**
 * Section and filling for an insulated conductor, at a given insulation Ø.
 *
 * `fillingSection` is the compound around the copper — insulation disc minus the
 * metal — and it is the number every consumption engine multiplies by. The old
 * create-standard form computed it as the engineer typed and showed it read-only;
 * this is that, extracted so all three forms compute it the same way.
 */
export function deriveIsolation(wire, diameter) {
  const d = Number(diameter);
  if (!(d > 0)) return {};
  const section = round4(disc(d));
  // Prefer a section the engineer entered by hand over one derived from the
  // build — a standard sometimes records the nominal rather than the geometric.
  const metal = Number(wire?.section) > 0 ? Number(wire.section) : wireMetalArea(wire);
  if (!(metal > 0)) return { section };
  return { section, fillingSection: round4(Math.max(0, section - metal)) };
}

/**
 * Whether a sheath is bedding — laid straight onto the conductors rather than
 * onto a solid cylinder — and therefore fills the voids between them.
 *
 * True over a rope, and true over bare conductors on a cable that has no rope
 * phase at all: MYYUP 2x1 puts its outer sheath on two cores and records
 * disc(sheath) − Σdisc(cores) to the last decimal on all four of its sizes.
 */
/**
 * Is this sheath the one sitting straight on the cores?
 *
 * Exported because there were three answers to it in three places — this one,
 * and two inside the view-standard edit modal — and they disagreed on the
 * cables that have no rope phase at all. See the note on the
 * `conductor_isolation` branch below.
 */
export function isBedding(phases, index) {
  const phase = phases[index];
  if (!phase) return false;
  if (phase.dependsOn === 'rope') return true;
  for (let i = index - 1; i >= 0; i--) {
    const prev = phases[i];
    // Nothing solid in between: the sheath is on the cores themselves. The 8
    // MYYUP flat cables are exactly this — `conductor_isolation` then
    // `exterior_sheat`, no rope, no stranding — and their stored filling is
    // disc − Σ disc(cores) to four decimal places. Walking past this phase
    // because it has no diameter of its own is how the edit modal came to treat
    // them as an annulus over nothing and write the whole disc as filling.
    if (prev?.name === 'conductor_isolation') return true;
    if (Number(prev?.diameter) > 0) return prev.name === 'rope';
  }
  return false;
}

/** Σ disc(insulated conductor) — what a bedding sheath actually displaces. */
export function sumCoreDiscs(phases, numberOfConductors) {
  const cond = conductorsOf(phases.find(p => p?.name === 'conductor_isolation'));
  const outers = cond.map(c => Number(c?.isolation?.diameter) || 0).filter(d => d > 0);
  if (!outers.length) return 0;
  const nc = Number(numberOfConductors) || outers.length;
  // One conductor described but several declared stands for all of them.
  if (outers.length === 1 && nc > 1) return round4(nc * disc(outers[0]));
  return round4(outers.reduce((s, d) => s + disc(d), 0));
}

/**
 * Section and filling for a chain phase at a given outer diameter.
 *
 * What a form fills in the instant the engineer types a diameter, so the numbers
 * on screen are the numbers that get saved. Left to the save step alone, a sheath
 * keeps `fillingSection: null` and then weighs nothing in all four consumption
 * engines without anything ever saying so.
 */
export function deriveAtDiameter({ phases = [], index, diameter, numberOfConductors } = {}) {
  const d = Number(diameter);
  const phase = phases[index];
  if (!phase || !(d > 0)) return {};

  const section = round4(disc(d));
  // Only a sheath has a filling. The result is spread over the phase being
  // edited, so a key that is merely absent leaves whatever is there alone —
  // returning `fillingSection: null` would wipe the rope's and the armour's
  // fields on every keystroke.
  if (phase.name !== 'interior_sheat' && phase.name !== 'exterior_sheat') return { section };

  let displaced = 0;
  if (isBedding(phases, index)) {
    displaced = sumCoreDiscs(phases, numberOfConductors);
  } else {
    for (let i = index - 1; i >= 0; i--) {
      const prev = Number(phases[i]?.diameter) || 0;
      if (prev > 0) { displaced = disc(prev); break; }
    }
  }

  // Nothing underneath filled in yet: say nothing rather than clear the field.
  if (!(displaced > 0)) return { section };
  return { section, fillingSection: round4(Math.max(0, section - displaced)) };
}

/** Conductors in either shape the data uses: a conductors[] array, or a flat pair. */
export const conductorsOf = phase => {
  if (Array.isArray(phase?.conductors) && phase.conductors.length) return phase.conductors;
  if (phase?.wire || phase?.isolation) return [{ wire: phase.wire, isolation: phase.isolation }];
  return [];
};

/**
 * Expected diameter, section and filling for every phase that can be derived.
 *
 * `pre_stranding` is absent on purpose: what comes off the stranding line is a
 * property of the wire and the lay, not of anything below it in the cable, so
 * it is entered rather than predicted.
 *
 * @returns {{ pred: object, expIsoMax: number, sumCoreDiscs: number }}
 *          `pred` is keyed by the phase's index in the array it was given.
 */
export function buildPrediction({ phases = [], numberOfConductors } = {}) {
  const pred = {};
  const ci = phases.find(p => p?.name === 'conductor_isolation');
  const cond = conductorsOf(ci);
  const nc = Number(numberOfConductors) || cond.length || 1;

  // Outer diameter of each insulated conductor. Computed from wire + wall where
  // both are known, falling back to whatever diameter is stored — a standard
  // half filled in still deserves a prediction for the phases above it.
  const coreOuters = cond
    .map(c => {
      const wireD = Number(c?.wire?.diameter) || 0;
      const r = avgRadial(c?.isolation?.minRadial, c?.isolation?.maxRadial);
      return wireD > 0 && r > 0 ? round4(wireD + 2 * r) : Number(c?.isolation?.diameter) || 0;
    })
    .filter(d => d > 0);

  const expIsoMax = coreOuters.length ? Math.max(...coreOuters) : 0;

  // One conductor described but several declared means the description is meant
  // to stand for all of them — the usual way a 4×1.5 is written down.
  const sumCoreDiscs = !coreOuters.length
    ? 0
    : coreOuters.length === 1 && nc > 1
      ? nc * disc(coreOuters[0])
      : coreOuters.reduce((s, d) => s + disc(d), 0);

  let prevD = expIsoMax;
  let prevLayer = 'conductor_isolation';

  phases.forEach((ph, i) => {
    if (!ph) return;

    if (ph.name === 'rope') {
      const d = expIsoMax > 0 ? round4(expIsoMax * packRatio(nc)) : 0;
      pred[i] = { diameter: d, section: round4(disc(d)) };
      if (d > 0) {
        prevD = d;
        prevLayer = 'rope';
      }
      return;
    }

    if (ph.name === 'interior_sheat' || ph.name === 'exterior_sheat') {
      const r = avgRadial(ph.minRadial, ph.maxRadial);
      const d = prevD > 0 && r > 0 ? round4(prevD + 2 * r) : 0;
      const section = round4(disc(d));

      // A sheath applied straight onto the rope also fills the gaps between the
      // conductors, so what it actually consumes is its own disc minus the
      // conductors', not minus the rope's circumscribed circle. Any sheath
      // further out sits on a solid cylinder and only adds its own wall.
      const bedding = prevLayer === 'rope';
      const filling =
        d > 0 ? round4(Math.max(0, section - (bedding ? sumCoreDiscs : disc(prevD)))) : 0;

      pred[i] = { diameter: d, section, fillingSection: filling };
      if (d > 0) {
        prevD = d;
        prevLayer = ph.name;
      }
      return;
    }

    if (ph.name === 'armouring') {
      const t = Number(ph.thickness) || 0;
      const d = prevD > 0 && t > 0 ? round4(prevD + 2 * t) : 0;
      pred[i] = { diameter: d, section: round4(disc(d)) };
      if (d > 0) {
        prevD = d;
        prevLayer = 'armouring';
      }
    }
  });

  return { pred, expIsoMax, sumCoreDiscs };
}

/**
 * Phases whose stored filling no longer follows from the diameters around them.
 *
 * Distinct from findStalePhases below, and the distinction is the point.
 * findStalePhases asks "does this diameter match the cascade's theory", which on
 * real cables is a disagreement 63% of the time and means nothing — a bedding
 * sheath over a twisted rope does not measure rope + 2 × wall. This asks the
 * question that always has one right answer: "does the filling still equal the
 * disc minus what it displaces, at the diameters actually written down".
 *
 * That goes wrong for exactly one reason: something underneath moved after this
 * phase was filled in. Widen a conductor and every sheath above it is quietly
 * measuring against cores that no longer exist. Nothing else can trip it, so it
 * never cries wolf on a measured value.
 */
export function findStaleDerivations(phases = [], numberOfConductors) {
  const out = [];
  const off = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) > 0.01;

  phases.forEach((ph, index) => {
    if (ph?.name === 'conductor_isolation') {
      conductorsOf(ph).forEach((c, ci) => {
        const iso = c?.isolation || {};
        if (!(Number(iso.diameter) > 0) || iso.fillingSection == null) return;
        const want = deriveIsolation(c?.wire, iso.diameter);
        if (want.fillingSection != null && off(iso.fillingSection, want.fillingSection)) {
          out.push({
            index,
            conductorIndex: ci,
            name: ph.name,
            label: `${PHASE_LABEL_FALLBACK[ph.name] || ph.name} — conductor ${ci + 1}`,
            stored: Number(iso.fillingSection),
            expected: want.fillingSection,
          });
        }
      });
      return;
    }

    if (ph?.name !== 'interior_sheat' && ph?.name !== 'exterior_sheat') return;
    if (!(Number(ph.diameter) > 0) || ph.fillingSection == null) return;
    const want = deriveAtDiameter({ phases, index, diameter: ph.diameter, numberOfConductors });
    if (want.fillingSection != null && off(ph.fillingSection, want.fillingSection)) {
      out.push({
        index,
        name: ph.name,
        label: PHASE_LABEL_FALLBACK[ph.name] || ph.name,
        stored: Number(ph.fillingSection),
        expected: want.fillingSection,
      });
    }
  });

  return out;
}

// Kept here rather than imported from standardPayload, which imports this module.
const PHASE_LABEL_FALLBACK = {
  conductor_isolation: 'IZOLARE',
  rope: 'INFUNIERE',
  interior_sheat: 'MANTA INTERIOARA',
  exterior_sheat: 'MANTA EXTERIOARA',
};

/**
 * Every stale filling recomputed, diameters untouched.
 *
 * The diameters are the engineer's measurements and are never overwritten; only
 * the numbers that are supposed to follow from them are brought back in line.
 */
export function recomputeDerived(phases = [], numberOfConductors) {
  return phases.map((ph, index) => {
    if (ph?.name === 'conductor_isolation') {
      const conductors = ph.conductors;
      if (!Array.isArray(conductors)) return ph;
      return {
        ...ph,
        conductors: conductors.map(c => {
          const iso = c?.isolation || {};
          if (!(Number(iso.diameter) > 0)) return c;
          return { ...c, isolation: { ...iso, ...deriveIsolation(c?.wire, iso.diameter) } };
        }),
      };
    }
    if (ph?.name !== 'interior_sheat' && ph?.name !== 'exterior_sheat') return ph;
    if (!(Number(ph.diameter) > 0)) return ph;
    return { ...ph, ...deriveAtDiameter({ phases, index, diameter: ph.diameter, numberOfConductors }) };
  });
}

/** The prediction for one phase, folded into it. */
export function applyPredictionTo(phase, p) {
  if (!p || !(p.diameter > 0)) return phase;
  return {
    ...phase,
    diameter: p.diameter,
    section: p.section,
    ...(p.fillingSection != null ? { fillingSection: p.fillingSection } : {})
  };
}

/** Every prediction folded in at once. */
export function applyAllPredictions(phases, numberOfConductors) {
  const { pred } = buildPrediction({ phases, numberOfConductors });
  return phases.map((ph, i) => applyPredictionTo(ph, pred[i]));
}

/**
 * Phases whose stored diameter no longer agrees with the cascade.
 *
 * The number that matters after an upstream edit: change a wire diameter and
 * every phase above it is quietly wrong until this says so.
 */
export function findStalePhases(phases = [], numberOfConductors) {
  const { pred } = buildPrediction({ phases, numberOfConductors });
  return phases.reduce((out, ph, i) => {
    const p = pred[i];
    const stored = Number(ph?.diameter) || 0;
    if (p && p.diameter > 0 && stored > 0 && Math.abs(stored - p.diameter) > 0.01) {
      out.push({ index: i, name: ph.name, stored, expected: p.diameter });
    }
    return out;
  }, []);
}
